import { Component, ChangeDetectionStrategy, OnInit, computed, signal, inject } from '@angular/core';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { filter, take, tap } from 'rxjs/operators';
import { IrisDmsGlobalSandbox } from '@iris/common/modules/dms/sandbox/dms-global.sandbox';
import { findIndex } from 'lodash';
import { TranslatePipe } from '@ngx-translate/core';
import { IrisDmsMatFoldersTreeComponent } from '../../folders-structures/dms-mat-folders-tree/dms-mat-folders-tree.component';
import { FormsModule } from '@angular/forms';
import { IrisFoldersTreeModalParamsI } from '../../../sandbox/dms-modals.sandbox';
import { StrakitButtonComponent, StrakitSelectComponent } from '@ngx-strakit/cdk/controls';
import { IrisProjectSkSelectDirective } from '@iris/common/modules/fields/project-sk-select/project-select.directive';
import { IrisSkSelectFieldDirective } from '@iris/common/modules/fields/sk-select-field/ng-select-field.sk.directive';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';
import { IrisDMSFolderI } from '../../../models/IrisDMSFolder';

@Component({
  selector: 'iris-folders-tree-modal',
  imports: [
    TranslatePipe,
    FormsModule,
    SkModalContentComponent,
    IrisSkSelectFieldDirective,
    IrisDmsMatFoldersTreeComponent,
    StrakitSelectComponent,
    IrisProjectSkSelectDirective,
    ConfirmByKeypressDirective,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './folders-tree-modal.component.html',
  styleUrls: ['./folders-tree-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisFoldersTreeModalComponent extends SkModalBase<IrisFoldersTreeModalComponent, IrisDMSFolderI[] | string> implements OnInit {
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly matDialog = inject(MatDialog);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly dmsGlobalSandbox = inject(IrisDmsGlobalSandbox);

  private readonly foldersTreeModalParams = signal<IrisFoldersTreeModalParamsI>(inject<IrisFoldersTreeModalParamsI>(MAT_DIALOG_DATA));
  readonly label = computed(() => this.foldersTreeModalParams()?.label ?? '');
  readonly multiselect = computed(() => this.foldersTreeModalParams()?.multiselect ?? false);
  readonly showSelectBtn = computed(() => this.foldersTreeModalParams()?.showSelectBtn ?? true);
  readonly showProjectSelector = computed(() => this.foldersTreeModalParams()?.showProjectSelector ?? false);
  readonly projectToExclude = computed(() => this.foldersTreeModalParams()?.projectToExclude ?? null);
  readonly foldersProjectId = computed(() => this.foldersTreeModalParams()?.foldersProjectId ?? null);

  private readonly selectedFolderId = this.dmsFoldersSandbox.selectedFolderIdModal;
  readonly selectedFolders = this.dmsFoldersSandbox.selectedFoldersListModal;
  readonly currentProjectId = this.dmsGlobalSandbox.currentProjectIdModal;

  readonly projectInfoMessage = computed(() => {
    return !this.currentProjectId() ? 'text.dms.PleaseSelectProject' : 'text.dms.ThisProjectDoNotHaveFolders';
  });

  readonly showFoldersTree = computed(() => {
    return !!this.currentProjectId() || this.dmsFoldersSandbox.foldersLoading();
  });

  readonly disableSubmit = computed(() => this.multiselect() ? !this.selectedFolders().length : !this.selectedFolderId());

  get isCurrentDialogLastOne(): boolean {
    return findIndex(this.matDialog.openDialogs, dialog => dialog.componentInstance === this) === this.matDialog.openDialogs.length - 1;
  }

  ngOnInit(): void {
    this.globalSandbox.currentProjectId$.pipe(
      filter(Boolean),
      take(1),
      tap(projectId => {
        if (projectId !== this.projectToExclude()) {
          if (!this.currentProjectId() && !this.foldersProjectId()) {
            this.projectChanged(projectId);
          }
          return;
        }

        if (this.projectToExclude()) {
          this.dmsFoldersSandbox.changeProjectFolderModal(null);
        }
      }),
    ).subscribe();

    if (this.foldersProjectId()) {
      this.projectChanged(this.foldersProjectId());
    }
  }

  confirmSelection(): void {
    if (this.disableSubmit() || !this.isCurrentDialogLastOne) { return; }
    this.closeWithResult(this.multiselect() ? this.selectedFolders() : this.selectedFolderId());
    this.dmsFoldersSandbox.resetSelectedFoldersListModal();
  }

  projectChanged(projectId: number): void {
    this.dmsFoldersSandbox.changeProjectFolderModal(projectId);
    if (!projectId) { return; }
    this.dmsFoldersSandbox.getProjectRootFolder(projectId);
    this.dmsFoldersSandbox.setSelectedFolderModal(null);
  }
}
